/** Display labels and helpers for account hierarchy (Superior Admin → Client Admin → sub-accounts). */

export type AccountRoleType =
  | 'admin'
  | 'agent'
  | 'support'
  | 'staff'
  | 'end_user';

export function isClientAdminUser(role?: string, parentAccountId?: string | null): boolean {
  return role === 'agent' && !parentAccountId?.trim();
}

export function isEndUserRole(role?: string): boolean {
  return role === 'end_user';
}

/** Nav entries hidden for end-user accounts (no account management). */
export const END_USER_HIDDEN_NAV_IDS = ['accounts'] as const;

export function isSubAccountUser(parentAccountId?: string | null): boolean {
  return Boolean(parentAccountId?.trim());
}

/** Superior Admin: pick role; optional parent for Dealer / User / Staff. */
export const ADMIN_CREATABLE_ROLES: { value: AccountRoleType; label: string; hint: string }[] = [
  {
    value: 'agent',
    label: 'Dealer',
    hint: 'Full dealer account: manage all devices and portal users in your subtree.',
  },
  {
    value: 'end_user',
    label: 'User',
    hint: 'Own merchant scope: manage devices, customers, plans, vouchers, and portal branding on own account only.',
  },
  {
    value: 'staff',
    label: 'Staff',
    hint: 'Assists the dealer: bind/unbind devices; no plans/vouchers/sales export or account creation.',
  },
];

/** Client Admin creates sub-accounts directly under their account. */
export const CLIENT_ADMIN_CREATABLE: { value: AccountRoleType; label: string; hint: string }[] = [
  {
    value: 'agent',
    label: 'Sub-Dealer',
    hint: 'Can transfer router SN within scope; manage devices and portal users in your branch.',
  },
  {
    value: 'staff',
    label: 'Staff',
    hint: 'Manage devices and portal users in your branch.',
  },
  {
    value: 'end_user',
    label: 'User',
    hint: 'Own merchant scope only; does not inherit dealer templates or transfer router ownership.',
  },
];

export function roleDisplayLabel(role: string, parentAccountId?: string | null): string {
  const sub = isSubAccountUser(parentAccountId);
  switch (role) {
    case 'admin':
      return 'Superior Admin';
    case 'agent':
      return sub ? 'Sub-Dealer' : 'Client Admin (Dealer)';
    case 'staff':
      return sub ? 'Staff' : 'Staff (legacy merchant)';
    case 'end_user':
      return sub ? 'User (sub)' : 'User';
    case 'support':
      return 'Support';
    default:
      return role;
  }
}

/** Header / dashboard badge: role label plus L-tier for distributors. */
export function userRoleBadgeLabel(
  role?: string,
  parentAccountId?: string | null,
  distributorLevel?: number
): string {
  const base = roleDisplayLabel(role ?? '', parentAccountId);
  if (role === 'agent' && typeof distributorLevel === 'number' && distributorLevel > 0) {
    return `${base} · L${distributorLevel}`;
  }
  return base;
}

/** Only Superior Admin may create accounts via management UI / API. */
export function canCreateAccounts(role?: string): boolean {
  return role === 'admin';
}

/** WiFi customer ops — dealer subtree + assisting staff + end-user on own devices. */
export function canManageCustomers(
  role?: string,
  parentAccountId?: string | null
): boolean {
  if (!role) return false;
  if (role === 'admin' || role === 'support') return true;
  if (role === 'agent' || role === 'end_user') return true;
  if (role === 'staff') {
    return true;
  }
  return false;
}

/** Dealer-level scope: all routers and portal users under the account subtree. */
export function canManageDealerSubtree(
  role?: string,
  parentAccountId?: string | null
): boolean {
  return canManageCustomers(role, parentAccountId);
}

/** Edit router settings (WiFi, name, hardware); not ownership transfer. */
export function canEditRouterConfig(role?: string): boolean {
  return (
    role === 'admin' ||
    role === 'agent' ||
    role === 'staff' ||
    role === 'end_user'
  );
}

/** Bind new device by SN or unbind from own account (not cross-account transfer). */
export function canBindUnbindDeviceBySn(role?: string): boolean {
  return role === 'admin' || role === 'agent' || role === 'staff' || role === 'end_user';
}

/** Reassign router to another account (change agent scope). End-user cannot. */
export function canTransferRouterSn(role?: string): boolean {
  return role === 'admin' || role === 'agent';
}

export function canExportSales(role?: string): boolean {
  return role === 'admin' || role === 'agent' || role === 'end_user';
}

export function canCreateServicePlans(role?: string): boolean {
  return role === 'admin' || role === 'support' || role === 'agent' || role === 'end_user';
}

export function canEditServicePlanContent(role?: string): boolean {
  return canCreateServicePlans(role);
}

export function canActivateOrBindPlans(role?: string): boolean {
  if (!role) return false;
  if (role === 'support') return false;
  return (
    role === 'admin' ||
    role === 'agent' ||
    role === 'staff' ||
    role === 'end_user'
  );
}

export function canManagePlansOrVouchers(role?: string): boolean {
  return canCreateServicePlans(role);
}

export function canCreateVouchers(role?: string): boolean {
  return role === 'admin' || role === 'support' || role === 'agent' || role === 'end_user';
}

export function isPlatformScopeStaff(role?: string, parentAccountId?: string | null): boolean {
  return role === 'admin' || role === 'support' || (role === 'staff' && !parentAccountId?.trim());
}

export function canAccessPortalBranding(role?: string, parentAccountId?: string | null): boolean {
  if (!role) return false;
  if (role === 'end_user') return true;
  if (isPlatformScopeStaff(role, parentAccountId)) return true;
  if (isClientAdminUser(role, parentAccountId)) return true;
  return role === 'staff' && Boolean(parentAccountId?.trim());
}

export function isSubResellerUser(role?: string, parentAccountId?: string | null): boolean {
  return role === 'agent' && Boolean(parentAccountId?.trim());
}

/** Only Superior Admin may open Accounts management or create accounts. */
export function canAccessAccounts(role?: string, _parentAccountId?: string | null): boolean {
  return role === 'admin';
}

/** Roles a Sub-Dealer may create (L2 max — cannot nest another dealer tier). */
export function subResellerCreatableRoles(distributorLevel?: number): typeof CLIENT_ADMIN_CREATABLE {
  if (typeof distributorLevel === 'number' && distributorLevel >= 2) {
    return CLIENT_ADMIN_CREATABLE.filter((r) => r.value !== 'agent');
  }
  return CLIENT_ADMIN_CREATABLE;
}

/** Superior Admin create form: optional Client Admin parent for Dealer / User / Staff. */
export function adminCreateShowsParentField(role: AccountRoleType): boolean {
  return role === 'agent' || role === 'end_user' || role === 'staff';
}

export function roleHintForCreate(
  role: AccountRoleType,
  isSuperiorAdmin: boolean,
  parentAccountId?: string
): string {
  const list = isSuperiorAdmin ? ADMIN_CREATABLE_ROLES : CLIENT_ADMIN_CREATABLE;
  const base = list.find((r) => r.value === role)?.hint ?? '';
  if (isSuperiorAdmin && (role === 'agent' || role === 'end_user' || role === 'staff')) {
    if (role === 'staff') {
      return parentAccountId?.trim()
        ? `Sub-account under the specified Client Admin. ${base}`
        : `Top-level Staff under Superior Admin. ${base}`;
    }
    if (role === 'agent') {
      return parentAccountId?.trim()
        ? `Sub-Dealer under the specified Client Admin. ${base}`
        : `Top-level Client Admin account. ${base}`;
    }
    return parentAccountId?.trim()
      ? `User under the specified Client Admin. ${base}`
      : `Top-level Client Admin account. ${base}`;
  }
  return base;
}

export type AccountRoleFilter = AccountRoleType | 'all';

export const ACCOUNT_ROLE_FILTERS: { value: AccountRoleFilter; label: string }[] = [
  { value: 'all', label: 'All roles' },
  { value: 'admin', label: 'Superior Admin' },
  { value: 'agent', label: 'Dealer' },
  { value: 'staff', label: 'Staff' },
  { value: 'end_user', label: 'User' },
  { value: 'support', label: 'Support' },
];
